import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import {
  FirebaseRealtimeDatabaseService,
  NotificationItem,
} from '../../firebase/firebase-realtime-database.service';
import { NotificationsService } from '../../services/notifications.service';
import { Router, NavigationExtras } from '@angular/router';

@Component({
  selector: 'app-notifications',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './notifications.component.html',
})
export class NotificationsComponent implements OnInit, OnDestroy {
  isOpen = false;
  notifications: NotificationItem[] = [];

  private notificationsSubscription?: Subscription;

  @ViewChild('container') container?: ElementRef<HTMLElement>;

  constructor(
    private readonly realtimeDatabase: FirebaseRealtimeDatabaseService,
    private readonly notificationsService: NotificationsService,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.notificationsSubscription = this.realtimeDatabase
      .listenToNotifications('notifications')
      .subscribe({
        next: (notifications) => (this.notifications = notifications),
        error: () => (this.notifications = []),
      });
  }

  ngOnDestroy(): void {
    this.notificationsSubscription?.unsubscribe();
  }

  get hasUnread(): boolean {
    return this.notifications.some((n) => !n.data.isRead);
  }

  formatTime(timestamp: number): string {
    const date = new Date(timestamp);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes < 10 ? `0${minutes}` : minutes;
    return `${displayHours}:${displayMinutes} ${ampm}`;
  }

  formatNotificationBody(body: string): string {
    if (!body) return '';
    // Reemplazar "a. m." con "am" y "p. m." con "pm" (case insensitive)
    return body
      .replace(/\ba\.\s*m\./gi, 'am')
      .replace(/\bp\.\s*m\./gi, 'pm');
  }

  toggle() {
    this.isOpen = !this.isOpen;
  }

  close() {
    this.isOpen = false;
  }

  private markAsRead(n: NotificationItem) {
    n.data.isRead = true;
    const notificationId = n.id;
    const userId = n.recipient.userId;
    if (notificationId && userId) {
      this.notificationsService.markAsRead(notificationId, userId).subscribe({
        error: () => {
          n.data.isRead = false;
        },
      });
    }
  }

  private routeFor(
    n: NotificationItem
  ): { commands: any[]; extras?: NavigationExtras } | null {
    
    const id = n.data.eventId;
    const type = n.data.eventtype || '';

    if (type === 'PAYMENT_REVISION') {
      return { commands: ['/payments'] };
    }    

    // Subastas - diferentes tipos de eventos de subastas
    if (type.startsWith('auction') || 
        type.startsWith('subasta') ||
        type.includes('auction')) {
      return id ? { commands: ['/verSubasta', id] } : { commands: ['/subastas'] };
    }

    // Lotes - diferentes tipos de eventos de lotes
    if (type.startsWith('lot') || 
        type.startsWith('lote') ||
        type.includes('lot')) {
      return id ? { commands: ['/verLote', id] } : { commands: ['/lotes'] };
    }

    // Participantes / Inscripciones / Registros
    if (
      type.startsWith('participant') ||
      type.startsWith('registration') ||
      type.startsWith('enrollment') ||
      type.startsWith('inscription') ||
      type.startsWith('inscrip') ||
      type.toLowerCase().includes('participant') ||
      type.toLowerCase().includes('inscrip') ||
      type.toLowerCase().includes('registro')
    ) {
      const extras: NavigationExtras | undefined = id
        ? { 
            queryParams: { 
              id, 
              event: type,
              openModal: 'true' // Parámetro adicional para indicar que se debe abrir el modal
            } 
          }
        : undefined;
      return { commands: ['/participantes'] };
    }

    // Eventos de creación/edición
    if (type.includes('create') || type.includes('crear')) {
      if (type.includes('auction') || type.includes('subasta')) {
        return { commands: ['/crear-subastas'] };
      }
      if (type.includes('lot') || type.includes('lote')) {
        return { commands: ['/crear'] };
      }
    }

    if (type.includes('edit') || type.includes('editar')) {
      if (type.includes('auction') || type.includes('subasta')) {
        return id ? { commands: ['/editar-subasta', id] } : { commands: ['/subastas'] };
      }
      if (type.includes('lot') || type.includes('lote')) {
        return id ? { commands: ['/editarinfolote', id] } : { commands: ['/lotes'] };
      }
    }

    // Eventos de información/completar
    if (type.includes('info') || type.includes('completar')) {
      if (type.includes('lot') || type.includes('lote')) {
        return id ? { commands: ['/completarinfolote', id] } : { commands: ['/lotes'] };
      }
    }

    // Eventos de estado/status
    if (type.includes('status') || type.includes('estado')) {
      if (type.includes('auction') || type.includes('subasta')) {
        return id ? { commands: ['/verSubasta', id] } : { commands: ['/subastas'] };
      }
      if (type.includes('lot') || type.includes('lote')) {
        return id ? { commands: ['/verLote', id] } : { commands: ['/lotes'] };
      }
    }

    //Evento para nueva carga de pago
    if (type === 'PAYMENT_UPDATED_AFTER_REJECTION') {
      return { commands: ['/participantes'] };
    }   

    if (type === 'LOT_RESCUE_APPROVED') {
      return { commands: ['/rescates'] };
    }   
    
    if (type === 'LOT_RESCUE_REJECTED') {
      return { commands: ['/rescates'] };
    }      
    
    if (type === 'rescueCreated') {
      return { commands: ['/rescates'] };
    }          

    // Eventos de notificación general
    if (type.includes('notification') || type.includes('notificacion')) {
      // Redirigir a la página principal si no hay contexto específico
      return { commands: ['/inicio'] };
    }

    // Por defecto, si no se puede determinar el tipo, redirigir al inicio
    return { commands: ['/inicio'] };
  }

  onNotificationClick(n: NotificationItem) {  
    // marcar como leída y cerrar panel
    this.markAsRead(n);
    this.close();

    const route = this.routeFor(n);
    if (route) {
      this.router.navigate(route.commands, route.extras);
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.isOpen) return;
    const target = event.target as Node | null;
    const el = this.container?.nativeElement;
    if (el && target && !el.contains(target)) {
      this.close();
    }
  }
}
